import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults sections
import { OrdinalTier, RankingBonusesTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// Tiers Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import { isFirstValid } from "@/Common/helpers/Matrices/tiers";

const minimumColumns = 1;
const maximumColumns = 7;
const maximumTiers = Number.MAX_VALUE;

const maximumRank = 15000;

export default function RankingBonus(props) {
	const {
		make = "",
		tiers = [],
		isSomeConfigured = true,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("regionalRanks");
	const rowsErrors = errors?.get("nationalRanks");
	const columnsErrors = errors?.get("bonuses");
	const firstBonusError = Boolean(errors?.get("range")?.length);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		onActionValidation?.(make, isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(make, updatedTiers);
		}
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Methods
	const handleAddColumn = () => {
		const updatedTiers = [...innerTiers];
		const bonusInput = updatedTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = updatedTiers?.[0]?.["bonuses"]?.length ? updatedTiers[0]["bonuses"].length - 1 : 0;

		updatedTiers.forEach((tier) => {
			tier.bonuses.push(OrdinalTier({ from: 0, to: maximumRank, bonus: 0, bonusInput }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = false;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumRank;
			});
		}
		update(updatedTiers);
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		const lastInput = updatedTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const previousBonusesLength = updatedTiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		for (let i = 0; i < previousBonusesLength; i++) {
			const previousFrom = updatedTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = updatedTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] || 0;
			bonuses[i] = OrdinalTier({ from: previousFrom, to: previousTo, bonus: 0, bonusInput: lastInput });
		}

		updatedTiers.push(RankingBonusesTier(0, maximumRank, bonuses));

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = false;
		setInnerTiers(updatedTiers);
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = updatedTiers?.length ? updatedTiers.length - 1 : 1;
		updatedTiers[lastIndex].to.value = maximumRank;
		update(updatedTiers);
	};

	const handleUpdateInput = (_row, _column, location, area, input) => {
		const updatedTiers = [...innerTiers];

		switch (location) {
			case "column":
				break;
			case "row":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(updatedTiers);
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus, key) => {
						if (area in bonus && key === column) {
							bonus[area]["value"] = updatedValue;
							if (tier.bonuses?.[key + 1]) {
								tier.bonuses[key + 1]["from"]["value"] = updatedValue + 1;
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "bonus") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = updatedValue + 1;
					}
				} else {
					updatedTiers[row]["bonuses"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update(updatedTiers);
	};

	const update = (updatedTiers = []) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "nationalRanks",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<=", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "regionalRanks",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<=", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Regional rank invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: ">=", // If current >= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: ">=", // If upper >= lower is good,
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "range",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Please add at least a two tiers configuration",
						operators: {
							customFn: (params = null) => {
								const { checkTiers = [], someConfigured = true } = params || {};
								const isSingleRows = checkTiers?.length === 1;
								const isSingleColumns = checkTiers?.[0]?.bonuses?.length === 1;
								const isSingleDefinition = isSingleRows && isSingleColumns;
								const firstValid = isFirstValid(checkTiers, { column: "bonuses", reference: "bonus" });

								const isGoodRange = !firstValid && someConfigured && isSingleDefinition;
								const isAlsoGoodRange = firstValid && !isSingleDefinition;

								const validRange = isGoodRange || isAlsoGoodRange;
								return [{ tiers: !validRange }];
							},
							params: {
								checkTiers: innerTiers,
								someConfigured: isSomeConfigured,
							},
						},
					},
					section: [{ tiers: false }],
				},
			]);
		}
	}, [innerTiers, isSomeConfigured, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	return (
		<Grid container item mt={2} xs={12}>
			<Grid item xs={12}>
				<TierColumns
					titleLabel={"Regional Rank"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "179px", width: "179px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerTiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
					disabledAdd={innerTiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.bonuses?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerTiers?.[0]?.bonuses.length === key + 1);

							return (
								<TierColumnsItem key={`column_ranking_bonus_${make}_${key}`} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>

			<Grid item xs={12} mt={1} minHeight={"135px"}>
				<TierRows
					showTitle
					title={"National Rank"}
					sxTitlePropsContainer={{
						top: 130,
						left: "-11px",
					}}
					centerTitleAfterRow={4}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 101,
						totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerTiers?.map((tier, key) => {
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerTiers?.length === key + 1);
									const bonusesLength = innerTiers?.[0]?.bonuses?.length || 0;
									const reduction = bonusesLength * 1.55 + 67 + bonusesLength;

									return (
										<Grid key={`row_ranking_bonus_${make}_${key}`} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													mainSection: 235,
													columns: 101,
													totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
													totalRows: innerTiers?.length || 1,
													separation: 5.5,
													reduction,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={columnsErrors || []}
												firstBonusError={firstBonusError}
												readOnly={readOnly}
												spacing={"9px"}
												sxMainContent={{ minWidth: "170.5px" }}
												sxCells={{ minWidth: "100.42px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_ranking_bonus_${message}_${key}`} text={message} />
							))}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
RankingBonus.propTypes = {
	make: PropTypes.string,
	tiers: PropTypes.array,
	isSomeConfigured: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
